import { motion, AnimatePresence } from 'motion/react';
import { Check, X, Palette } from 'lucide-react';
import { THEMES, ThemeId } from '../contexts/themeRegistry';

interface ThemeCenterProps {
  open: boolean;
  onClose: () => void;
  current: ThemeId;
  onSelect: (id: ThemeId) => void;
}

export function ThemeCenter({ open, onClose, current, onSelect }: ThemeCenterProps) {
  return (
    <AnimatePresence>
      {open && (
        <motion.div
          className="fixed inset-0 z-[200] flex items-center justify-center p-4"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
        >
          <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={onClose} />

          <motion.div
            initial={{ scale: 0.95, y: 20, opacity: 0 }}
            animate={{ scale: 1, y: 0, opacity: 1 }}
            exit={{ scale: 0.95, y: 20, opacity: 0 }}
            transition={{ type: 'spring', stiffness: 320, damping: 30 }}
            className="relative w-full max-w-4xl max-h-[85vh] overflow-hidden rounded-[2rem] border border-white/10 bg-black/80 backdrop-blur-2xl shadow-2xl"
          >
            <div className="flex items-center justify-between px-8 py-6 border-b border-white/10">
              <div className="flex items-center gap-3">
                <Palette className="text-celestial-saturn" size={22} />
                <div>
                  <h2 className="text-xl font-black text-white">主题中心</h2>
                  <p className="text-xs text-white/40">点击任意主题即时换肤 · 全局 token 驱动 · 自动记忆</p>
                </div>
              </div>
              <button
                onClick={onClose}
                className="w-9 h-9 flex items-center justify-center rounded-xl border border-white/10 bg-white/5 text-white/50 hover:text-white hover:bg-white/10 transition-colors"
              >
                <X size={18} />
              </button>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 p-8 overflow-y-auto max-h-[calc(85vh-96px)] custom-scrollbar">
              {THEMES.map((theme) => (
                <ThemeCard
                  key={theme.id}
                  theme={theme}
                  active={current === theme.id}
                  onClick={() => onSelect(theme.id)}
                />
              ))}
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

function ThemeCard({
  theme,
  active,
  onClick,
}: {
  theme: (typeof THEMES)[number];
  active: boolean;
  onClick: () => void;
}) {
  return (
    <button
      onClick={onClick}
      className={`group relative overflow-hidden rounded-2xl border p-4 text-left transition-all ${
        active
          ? 'border-celestial-saturn bg-celestial-saturn/10 shadow-[0_0_30px_rgba(255,204,0,0.12)]'
          : 'border-white/10 bg-white/5 hover:bg-white/10'
      }`}
    >
      {/* 实时预览：用 data-theme 包裹，色板直接读取该主题真实 token */}
      <div
        data-theme={theme.id}
        data-mode={theme.light ? 'light' : 'dark'}
        className="relative mb-4 h-24 rounded-xl overflow-hidden border border-white/10"
      >
        <div className="absolute inset-0 bg-celestial-deep" />
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-36 h-36 rounded-full bg-celestial-saturn/30 blur-2xl" />
        <div className="absolute top-3 left-3 right-3 space-y-1.5">
          <div className="h-1.5 w-2/3 rounded-full bg-celestial-saturn/70" />
          <div className="h-1.5 w-1/2 rounded-full bg-celestial-glow/60" />
        </div>
        <div className="absolute bottom-3 left-3 right-3 flex items-center justify-between">
          <span className="text-sm font-black text-celestial-saturn">Aa 小枫</span>
          <span className="h-2.5 w-2.5 rounded-full bg-celestial-glow shadow-[0_0_10px_var(--color-celestial-glow)]" />
        </div>
      </div>

      <div className="flex items-center justify-between">
        <div>
          <div className="text-sm font-black text-white">
            {theme.name}
            {theme.tag && <span className="ml-1.5 text-[10px] font-bold text-white/40">方案{theme.tag}</span>}
          </div>
          <div className="text-[11px] text-white/40">{theme.en}</div>
        </div>
        {active && (
          <span className="flex items-center gap-1 text-[10px] font-black text-celestial-saturn">
            <Check size={16} /> 使用中
          </span>
        )}
      </div>
      <p className="mt-2 text-[11px] text-white/45 leading-snug">{theme.desc}</p>
    </button>
  );
}
